/**
 * User-level analytics opt-out ("object to processing").
 *
 * #268 added an `analyticsEnabled` boolean to the user payload returned by
 * `/api/auth/me` and surfaced on the client's `dbUser`. When the user objects,
 * `analyticsEnabled` is `false` and client-side capture must be suppressed for
 * that user regardless of consent (server-side enforcement lives in #268/#266;
 * this is the client-side half).
 *
 * The field is now a required, typed `boolean` on `User`, so the accessor reads
 * it directly off a typed `User | null` — `null` is the signed-out case.
 */

import type { User as DbUser } from '@otalytics/shared';

/**
 * Custom event dispatched on `window` whenever the current user's opt-out
 * preference changes, mirroring the consent-change events so the provider can
 * re-evaluate capture without polling.
 */
export const USER_OPTOUT_CHANGED_EVENT = 'otalytics:user-optout-changed';

// Module-level cache of the current user's objection. `false` (default) means
// "not objected". Kept here so the gating layer can consult it synchronously;
// it is published by the auth layer via setUserAnalyticsPreference().
let userObjected = false;

/**
 * Whether the given user has objected to analytics. `analyticsEnabled` is a
 * required boolean on the user payload (#268), so we read it directly: only an
 * explicit `false` objects. A `null` user (signed out) is "not objected".
 */
export function readAnalyticsOptOut(dbUser: DbUser | null): boolean {
  return dbUser?.analyticsEnabled === false;
}

/**
 * Publish the current user's preference (called by the auth layer whenever
 * `dbUser` changes, and with `null` on sign-out). Fires a change event so a
 * mounted provider re-applies capture immediately.
 */
export function setUserAnalyticsPreference(dbUser: DbUser | null): void {
  const objected = readAnalyticsOptOut(dbUser);
  if (objected === userObjected) {
    return;
  }

  userObjected = objected;

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(USER_OPTOUT_CHANGED_EVENT, { detail: objected }));
  }
}

/**
 * Whether the current user has objected to analytics. Consulted by the gating
 * layer; `true` forces capture off.
 */
export function isUserObjected(): boolean {
  return userObjected;
}
